import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, mxn, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { useDarkMode } from "@/components/ThemeToggle";
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from "recharts";
import { ArrowUpRight, AlertTriangle, FileText, ClipboardList, Truck, TrendingUp, BarChart3, ArrowUp, ArrowDown } from "lucide-react";
import { FolioTag } from "@/components/StatusBadge";

// Colores fijos por año para el comparativo de ventas (año actual = azul institucional).
const YEAR_COLORS = {
  2020: "#E08E3F",
  2021: "#DD6B4C",
  2022: "#C4587A",
  2023: "#9B5DE0",
  2024: "#5B6FD8",
  2025: "#3B8FD4",
};

// Paleta institucional TAS Publicidad 360 (tonos sólidos por sección).
const TONES = {
  blue:   { bg: "#EDF1F8", chip: "#465B7F", title: "#2E3E58", value: "#16202B" },
  teal:   { bg: "#E5F3EF", chip: "#2F8F7E", title: "#1F6157", value: "#16202B" },
  coral:  { bg: "#FCECEA", chip: "#E0574F", title: "#8A2A26", value: "#16202B" },
  green:  { bg: "#ECF6EB", chip: "#4E9E58", title: "#2F6B39", value: "#16202B" },
  amber:  { bg: "#FBF3DA", chip: "#E0A93B", title: "#7A5A08", value: "#16202B" },
  purple: { bg: "#F0ECF8", chip: "#7C6BB0", title: "#463A73", value: "#16202B" },
};

// Variantes oscuras (azul marino profundo) de las tarjetas del panel.
const TONES_DARK = {
  blue:   { bg: "#122036", chip: "#7C94C0", title: "#AEC0DE", value: "#E7EDF6" },
  teal:   { bg: "#0F2A2A", chip: "#5FB5A6", title: "#9AD3C8", value: "#E7EDF6" },
  coral:  { bg: "#2A1618", chip: "#E48A83", title: "#E9A7A2", value: "#F1E3E1" },
  green:  { bg: "#14261A", chip: "#5FB56A", title: "#A6D9AE", value: "#E7EDF6" },
  amber:  { bg: "#26200F", chip: "#E0A93B", title: "#E3C98A", value: "#F3ECDA" },
  purple: { bg: "#1E1836", chip: "#9E8BE0", title: "#C6BAE9", value: "#E7EDF6" },
};

// Colores de estatus de Órdenes de Trabajo (mismos definidos en el módulo Órdenes).
const OT_STATUS_STYLES = {
  "Impresión Interna": "bg-[#FACC15] text-[#713F12] border-[#EAB308]",
  "Impresión Externa": "bg-[#FACC15] text-[#713F12] border-[#EAB308]",
  "Pendiente": "bg-[#EF4444] text-white border-[#DC2626]",
  "Acabado Interno": "bg-[#FDBA74] text-[#7C2D12] border-[#FB923C]",
  "Acabado Externo": "bg-[#EA580C] text-white border-[#C2410C]",
  "Terminada": "bg-[#22C55E] text-white border-[#16A34A]",
  "Entregada": "bg-[#15803D] text-white border-[#166534]",
};

// Badges de estatus de Cotizaciones — mismo estilo sólido que las Órdenes de Trabajo.
// Claves en minúsculas + lookup normalizado para tolerar cualquier variante de la API.
const QUOTE_STATUS_STYLES = {
  aprobada: "bg-[#22C55E] text-white border-[#16A34A]",
  rechazada: "bg-[#EF4444] text-white border-[#DC2626]",
  vencida: "bg-[#EF4444] text-white border-[#DC2626]",
  anulado: "bg-[#EF4444] text-white border-[#DC2626]",
  enviada: "bg-[#465B7F] text-white border-[#374A66]",
  borrador: "bg-[#94A3B8] text-white border-[#64748B]",
  "por enviar": "bg-[#94A3B8] text-white border-[#64748B]",
};
// Regla de oro: SIEMPRE con color de fondo (default gris sólido). Ignora mayúsculas/minúsculas.
const quoteStatusCls = (s) => QUOTE_STATUS_STYLES[String(s || "").trim().toLowerCase()] || "bg-[#94A3B8] text-white border-[#64748B]";

// Mapeo de estado de facturación (mismos colores que el módulo de Remisiones).
const FACT_BADGE = {
  facturada: { label: "Facturada", cls: "bg-[#49AC9A]/15 text-[#49AC9A] border-[#49AC9A]/30" },
  pendiente: { label: "Pendiente", cls: "bg-[#FDC50D]/15 text-[#B8860B] border-[#FDC50D]/40" },
  no_aplica: { label: "No requiere", cls: "bg-stone-200 text-stone-600 border-stone-300" },
};

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [vc, setVc] = useState(null);
  const [bgVisibility, setBgVisibility] = useState(30);
  const dark = useDarkMode();
  const T = dark ? TONES_DARK : TONES;

  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data));
    api.get("/dashboard/ventas-comparativo").then((r) => setVc(r.data)).catch(() => {});
    api.get("/settings").then((r) => {
      const v = r.data?.dashboard?.bg_visibility;
      if (typeof v === "number") setBgVisibility(v);
    }).catch(() => {});
  }, []);

  // Alpha de la capa oscura: más visibilidad => menos capa (imagen más visible).
  const overlayAlpha = Math.max(0.05, Math.min(0.95, (100 - bgVisibility) / 100));

  if (!d)
    return (
      <>
        <PageHeader overline="Resumen general" title="Panel" />
        <div className="p-10 font-mono-data text-[#5C5A55] animate-pulse">Cargando métricas…</div>
      </>
    );

  return (
    <div className="relative min-h-screen" data-testid="dashboard-root">
      {/* Fondo exclusivo del Dashboard (cubre toda la pantalla, no afecta otros módulos) */}
      <div
        className="fixed top-0 bottom-0 left-20 right-0 z-0 pointer-events-none"
        data-testid="dashboard-background"
        style={{
          backgroundImage: "url(/dashboard-bg.webp)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* Capa semitransparente para legibilidad del contenido */}
        <div className="absolute inset-0" style={{ backgroundColor: `rgba(9,16,28,${overlayAlpha})` }} />
      </div>

      {/* Contenido por encima del fondo */}
      <div className="relative z-10">
        {/* Encabezado transparente para que el fondo del Dashboard sea visible */}
        <div className="flex items-end justify-between border-b border-white/15 px-10 py-8" data-testid="dashboard-header">
          <div>
            <div className="overline" style={{ color: "rgba(255,255,255,0.7)" }}>Resumen general</div>
            <h1 className="font-display font-extrabold text-4xl tracking-tight mt-1" style={{ color: "#FFFFFF", textShadow: "0 1px 12px rgba(0,0,0,0.45)" }}>
              Panel
            </h1>
          </div>
        </div>

        <div className="p-10 space-y-6">
        {/* Barra de acento de marca */}
        <div className="flex items-center gap-1.5" data-testid="brand-accent">
          {["#ED1F26", "#E0A93B", "#4E9E58", "#465B7F", "#7C6BB0"].map((c) => (
            <span key={c} className="h-1.5 w-9 rounded-full" style={{ backgroundColor: c }} />
          ))}
        </div>

        {/* Cotizaciones pendientes de remisionar + Remisiones + Órdenes de trabajo (misma fila, 3 columnas) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          {/* Cotizaciones · Pendientes de remisionar */}
          <div className="border rounded-xl p-6 lg:order-3" data-testid="quotes-pending-section"
            style={{ backgroundColor: T.amber.bg, borderColor: `${T.amber.chip}2E` }}>
            <div className="flex items-center gap-3 mb-5">
              <span className="flex items-center justify-center h-8 w-8 rounded-full" style={{ backgroundColor: `${T.amber.chip}26` }}>
                <FileText size={16} style={{ color: T.amber.chip }} />
              </span>
              <div className="overline" style={{ color: T.amber.title }}>Cotizaciones · Pendientes de remisionar</div>
            </div>
            {(!d.quotes_pending_remision || d.quotes_pending_remision.length === 0) ? (
              <p className="font-mono-data text-sm" style={{ color: T.amber.title }}>Todas las cotizaciones están remisionadas. ✅</p>
            ) : (
              <div className="space-y-3">
                {d.quotes_pending_remision.map((q) => (
                  <Link key={q.id} to={`/documento/cotizacion/${q.id}`} className="block group" data-testid={`pending-quote-${q.number}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono-data text-sm text-[#1C1A17] dark:text-[#E7EDF6] truncate">{q.number} · {q.client?.name || "—"}</span>
                      <span data-testid={`pending-quote-status-${q.number}`}
                        className={`shrink-0 border px-2 py-0.5 font-mono-data text-[10px] font-bold rounded ${quoteStatusCls(q.status)}`}>
                        {String(q.status || "").toLowerCase() === "borrador" ? "Por enviar" : (q.status || "—")}
                      </span>
                    </div>
                    <div className="text-xs text-[#7A5A08] dark:text-[#E3C98A] mt-0.5">{fmtDate(q.created_at)}</div>
                  </Link>
                ))}
              </div>
            )}
            <Link to="/cotizaciones" className="mt-6 inline-flex items-center gap-1 text-sm hover:gap-2 transition-all font-medium" style={{ color: T.amber.chip }}>
              Ir a Cotizaciones <ArrowUpRight size={15} />
            </Link>
          </div>

          {/* Remisiones recientes */}
          <div className="border rounded-xl p-6 lg:order-2" data-testid="remisiones-section" style={{ backgroundColor: T.blue.bg, borderColor: `${T.blue.chip}2E` }}>
            <div className="flex items-center gap-3 mb-5">
              <span className="flex items-center justify-center h-8 w-8 rounded-full" style={{ backgroundColor: `${T.blue.chip}26` }}>
                <Truck size={16} style={{ color: T.blue.chip }} />
              </span>
              <div className="overline" style={{ color: T.blue.title }}>Remisiones</div>
            </div>
            {(!d.remisiones_list || d.remisiones_list.length === 0) ? (
              <p className="font-mono-data text-sm" style={{ color: T.blue.title }}>Sin remisiones registradas.</p>
            ) : (
              <div className="space-y-3">
                {d.remisiones_list.map((r) => (
                  <Link key={r.id} to={`/documento/remision/${r.id}`} className="block group" data-testid={`remision-item-${r.number}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono-data text-sm text-[#1C1A17] dark:text-[#E7EDF6] truncate">{r.number} · {r.client?.name || "—"}</span>
                      {(() => {
                        const fb = FACT_BADGE[r.estado_facturacion] || FACT_BADGE.no_aplica;
                        return (
                          <span data-testid={`remision-facturacion-${r.number}`}
                            className={`shrink-0 inline-flex items-center border px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-mono-data ${fb.cls}`}>
                            {fb.label}
                          </span>
                        );
                      })()}
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-[#5C5A55] flex items-center gap-1.5">
                        OT: {(r.ot_folio || r.ot_number) ? <FolioTag number={r.ot_folio || r.ot_number} kind="ot" testid={`remision-ot-${r.number}`} /> : <span className="text-[#98A2AE]">Pendiente</span>}
                      </span>
                      <span className="text-xs text-[#5C5A55]">{fmtDate(r.created_at)}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
            <Link to="/remisiones" className="mt-6 inline-flex items-center gap-1 text-sm hover:gap-2 transition-all font-medium" style={{ color: T.blue.chip }}>
              Ir a Remisiones <ArrowUpRight size={15} />
            </Link>
          </div>

          {/* Órdenes de trabajo activas */}
          <div className="border rounded-xl p-6 lg:order-1" data-testid="active-orders-section" style={{ backgroundColor: T.teal.bg, borderColor: `${T.teal.chip}2E` }}>
            <div className="flex items-center gap-3 mb-5">
              <span className="flex items-center justify-center h-8 w-8 rounded-full" style={{ backgroundColor: `${T.teal.chip}26` }}>
                <ClipboardList size={16} style={{ color: T.teal.chip }} />
              </span>
              <div className="overline" style={{ color: T.teal.title }}>Órdenes de trabajo</div>
            </div>
            {(!d.active_orders_list || d.active_orders_list.length === 0) ? (
              <p className="font-mono-data text-sm text-[#1F6157] dark:text-[#9AD3C8]">Sin órdenes activas. ✅</p>
            ) : (
              <div className="space-y-3">
                {d.active_orders_list.map((o) => (
                  <Link key={o.id} to="/ordenes" className="block group" data-testid={`active-order-${o.number}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono-data text-sm text-[#1C1A17] dark:text-[#E7EDF6] truncate flex items-center gap-1.5">
                        <FolioTag number={o.number} kind="ot" testid={`active-order-folio-${o.number}`} /> {o.client?.name || o.title || "—"}
                      </span>
                      <span className={`shrink-0 border px-2 py-0.5 font-mono-data text-[10px] font-bold rounded ${OT_STATUS_STYLES[o.status] || "bg-white text-[#16202B] dark:text-[#E7EDF6] border-[#E3E8EE]"}`}>
                        {o.status}
                      </span>
                    </div>
                    {o.title && o.client?.name && (
                      <div className="text-xs text-[#1F6157] dark:text-[#9AD3C8] truncate mt-0.5">{o.title}</div>
                    )}
                  </Link>
                ))}
              </div>
            )}
            <Link to="/ordenes" className="mt-6 inline-flex items-center gap-1 text-sm hover:gap-2 transition-all font-medium" style={{ color: T.teal.chip }}>
              Ir a Órdenes de Trabajo <ArrowUpRight size={15} />
            </Link>
          </div>
        </div>

        {/* Ventas totales + Cobranza por mes (misma fila) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Ventas totales */}
          <div className="lg:col-span-2 border rounded-xl p-8" style={{ backgroundColor: T.blue.bg, borderColor: `${T.blue.chip}2E` }}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center h-8 w-8 rounded-full" style={{ backgroundColor: `${T.blue.chip}26` }}>
                  <TrendingUp size={16} style={{ color: T.blue.chip }} />
                </span>
                <div className="overline" style={{ color: T.blue.title }}>Ventas totales</div>
              </div>
              <div className="font-mono-data text-sm font-semibold" style={{ color: T.blue.value }} data-testid="metric-total-sales">{mxn(d.total_sales)}</div>
            </div>
            {(!d.sales_series || d.sales_series.length === 0) ? (
              <div className="font-mono-data text-sm py-16 text-center" style={{ color: T.blue.title }}>
                Aún no hay ventas facturadas.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={d.sales_series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="salesFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#465B7F" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#465B7F" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#465B7F1F" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontFamily: "Montserrat", fontSize: 11, fill: "#5C5A55" }} axisLine={{ stroke: "#465B7F33" }} tickLine={false} />
                  <YAxis tick={{ fontFamily: "Montserrat", fontSize: 11, fill: "#5C5A55" }} axisLine={false} tickLine={false} width={70} tickFormatter={(v) => mxn(v)} />
                  <Tooltip formatter={(v) => mxn(v)} contentStyle={{ fontFamily: "Montserrat", fontSize: 12, borderRadius: 8, border: "1px solid #E3E8EE", background: "#FFFFFF" }} />
                  <Area type="monotone" dataKey="amount" stroke="#465B7F" strokeWidth={2} fill="url(#salesFill)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Cobranza por mes */}
          <div className="border rounded-xl p-8" style={{ backgroundColor: T.teal.bg, borderColor: `${T.teal.chip}2E` }}>
            <div className="flex items-center gap-3 mb-6">
              <span className="flex items-center justify-center h-8 w-8 rounded-full" style={{ backgroundColor: `${T.teal.chip}26` }}>
                <BarChart3 size={16} style={{ color: T.teal.chip }} />
              </span>
              <div className="overline" style={{ color: T.teal.title }}>Cobranza por mes</div>
            </div>
            {d.revenue_series.length === 0 ? (
              <div className="font-mono-data text-sm py-16 text-center" style={{ color: T.teal.title }}>
                Aún no hay pagos registrados.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={d.revenue_series}>
                  <XAxis dataKey="month" tick={{ fontFamily: "Montserrat", fontSize: 11, fill: "#5C5A55" }} axisLine={{ stroke: "#2F8F7E33" }} tickLine={false} />
                  <Tooltip formatter={(v) => mxn(v)} contentStyle={{ fontFamily: "Montserrat", fontSize: 12, borderRadius: 8, border: "1px solid #E3E8EE", background: "#FFFFFF" }} />
                  <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                    {d.revenue_series.map((_, i) => (
                      <Cell key={i} fill="#2F8F7E" />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Ventas Totales — comparativo mensual (fila completa) */}
        {vc && <VentasComparativo vc={vc} />}

        {/* Resumen final */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <SummaryCard tone="coral" label="Saldo por cobrar" testid="metric-to-collect" big
            value={mxn(d.to_collect)}
            sub={`de ${mxn(d.total_billed)} facturado · ${mxn(d.total_paid)} cobrado`} />
          <SummaryCard tone="green" label="Cobrado total" testid="metric-paid" value={mxn(d.total_paid)} />
          <SummaryCard tone="purple" label="Facturas emitidas" testid="metric-invoices" value={d.invoices_count} />
        </div>

        {/* Tarjetas KPI (movidas al final) */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          <KpiCard tone="blue" icon={FileText} label="Cotizaciones" value={d.quotes_count} sub={`${d.quotes_pending} pendientes`} />
          <KpiCard tone="teal" icon={ClipboardList} label="Órdenes activas" value={d.active_orders} />
          <KpiCard tone="coral" icon={AlertTriangle} label="Vencidas" value={d.overdue_count} sub={mxn(d.overdue_amount)} valueColor="#C0392B" />
        </div>
        </div>
      </div>
    </div>
  );
}

function VentasComparativo({ vc }) {
  const dark = useDarkMode();
  const [active, setActive] = useState(() => new Set(vc.years));
  const toggle = (y) =>
    setActive((prev) => {
      const n = new Set(prev);
      if (n.has(y)) n.delete(y);
      else n.add(y);
      return n;
    });
  const colorFor = (y) => (y === vc.current_year ? "#185FA5" : YEAR_COLORS[y] || "#94A3B8");
  const up = vc.variation_pct != null && vc.variation_pct >= 0;

  return (
    <div className="border rounded-2xl p-8 shadow-sm bg-white dark:bg-[#10203A]" style={{ borderColor: dark ? "#1D3454" : "#185FA522" }} data-testid="ventas-comparativo-card">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-6">
        <div>
          <div className="overline" style={{ color: dark ? "#AEC0DE" : "#0F4278" }}>Ventas Totales — comparativo mensual</div>
          <div className="mt-2 flex items-end gap-3 flex-wrap">
            {vc.variation_pct != null && (
              <span data-testid="vc-variation" className="inline-flex items-center gap-1 text-sm font-bold px-2.5 py-1 rounded-full"
                style={{ backgroundColor: up ? "#E5F3EF" : "#FCECEA", color: up ? "#1F6157" : "#8A2A26" }}>
                {up ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
                {Math.abs(vc.variation_pct)}%
              </span>
            )}
          </div>
          <div className="font-mono-data text-xs mt-1 text-[#5C5A55]">
            Acumulado {vc.current_year} a la fecha · vs mismo periodo {vc.prev_year}
          </div>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="vc-chips">
          {vc.years.map((y) => {
            const on = active.has(y);
            const c = colorFor(y);
            return (
              <button key={y} onClick={() => toggle(y)} data-testid={`vc-chip-${y}`}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold transition-all"
                style={on ? { backgroundColor: c, borderColor: c, color: "#fff" } : { backgroundColor: "#fff", borderColor: "#E3E8EE", color: "#5C5A55" }}>
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: on ? "#fff" : c }} />
                {y}{y === vc.current_year ? " (actual)" : ""}
              </button>
            );
          })}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={340}>
        <BarChart data={vc.series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }} barCategoryGap="18%">
          <CartesianGrid strokeDasharray="3 3" stroke="#185FA51A" vertical={false} />
          <XAxis dataKey="month" tick={{ fontFamily: "Montserrat", fontSize: 12, fill: "#5C5A55" }} axisLine={{ stroke: "#185FA533" }} tickLine={false} />
          <YAxis tick={{ fontFamily: "Montserrat", fontSize: 11, fill: "#5C5A55" }} axisLine={false} tickLine={false} width={80} tickFormatter={(v) => mxn(v)} />
          <Tooltip cursor={{ fill: "#185FA50D" }} formatter={(value, name) => [mxn(value), name]}
            contentStyle={{ fontFamily: "Montserrat", fontSize: 12, borderRadius: 10, border: "1px solid #E3E8EE", background: "#FFFFFF" }} />
          {vc.years.filter((y) => active.has(y)).map((y) => (
            <Bar key={y} dataKey={String(y)} name={String(y)} fill={colorFor(y)} radius={[6, 6, 0, 0]}
              stroke={y === vc.current_year ? "#0F4278" : undefined} strokeWidth={y === vc.current_year ? 1.5 : 0} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function KpiCard({ tone, icon: Icon, label, value, sub, testid, valueColor }) {
  const dark = useDarkMode();
  const t = (dark ? TONES_DARK : TONES)[tone];
  return (
    <div className="p-6 border rounded-xl" style={{ backgroundColor: t.bg, borderColor: `${t.chip}2E` }}>
      <span className="flex items-center justify-center h-9 w-9 rounded-full mb-4" style={{ backgroundColor: `${t.chip}26` }}>
        <Icon size={18} style={{ color: t.chip }} />
      </span>
      <div className="overline" style={{ color: t.title }}>{label}</div>
      <div data-testid={testid} className="font-display font-bold text-3xl tracking-tight mt-1.5 font-mono-data"
        style={{ color: valueColor || t.value }}>
        {value}
      </div>
      {sub && <div className="font-mono-data text-xs mt-1" style={{ color: t.title, opacity: 0.85 }}>{sub}</div>}
    </div>
  );
}

function SummaryCard({ tone, label, value, sub, testid, big }) {
  const dark = useDarkMode();
  const t = (dark ? TONES_DARK : TONES)[tone];
  return (
    <div className="p-8 border rounded-xl" style={{ backgroundColor: t.bg, borderColor: `${t.chip}2E` }}>
      <div className="overline" style={{ color: t.title }}>{label}</div>
      <div data-testid={testid}
        className={`font-display font-black ${big ? "text-5xl" : "text-3xl"} tracking-tighter mt-2 font-mono-data`}
        style={{ color: t.value }}>
        {value}
      </div>
      {sub && <div className="font-mono-data text-xs mt-2" style={{ color: t.title, opacity: 0.85 }}>{sub}</div>}
    </div>
  );
}
